import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import ProtectedRoute from "./components/AdminProtectedRoute";
import React, { lazy, Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loading from "@shared/components/Loading";
import { getIsCourierLoading, fetchCourier, getCourierError, getOnboardingStatus, getCourier } from "./state/courierSlice";
import { getAppBootstrapView } from "./utils/appBootstrapView";
import { getIsAuthenticated, getUser } from "./state/authSlice";
import { Toaster } from "react-hot-toast";
import { ThemeProvider } from "./contexts/ThemeContext";
import { init } from "./state/authSlice";
import { AppDispatch } from "./store";
import ScrollToTop from "./components/ScrollToTop";
import { checkForRefreshParam, forceRefresh } from "./pwaRegistration";
import OnboardingRedirect from "./components/OnboardingRedirect";
import ErrorBoundary from "./components/ErrorBoundary";
import PocketBaseProvider from "./pocketbase/PocketBaseProvider";
import { useVersionCheck } from "./hooks/useVersionCheck";
import { usePartnerLinkRefresh } from "./hooks/usePartnerLinkRefresh";
import {
  AuthRoutePath,
  ResetPasswordRoutePath,
  DashboardRoute,
  CustomersRoute,
  PackagesRoute,
  PosRoute,
  InvoicesRoute,
  ManifestsRoute,
  ScanInRoute,
  SmartScanRoute,
  DeliveryRequestRoute,
  LocalDeliveriesRoute,
  CommunicationRoute,
  CommunicationLogsRoute,
  AssistantRoute,
  WhatsappRoute,
  MarketingRoute,
  ReviewsGalleryRoute,
  ExpensesRoute,
  CustomsEstimatorRoute,
  TariffCodesRoute,
  MarketingAgentsRoute,
  WarehouseRoute,
  WarehouseHubRoute,
  ScanBonusReportRoute,
  WarehouseBillingRoute,
  WAREHOUSE_INVOICES_ROUTE,
  InvoiceRequestsRoute,
  WarehouseInterestsRoute,
  UnknownPackagesRoute,
  FulfillmentPartnersRoute,
  FulfillmentOrdersRoute,
  PartnerRequirementsRoute,
  PartnerComplianceRoute,
  UsersRoute,
  SettingsRoute,
  BillingRoute,
  CouriersRoute,
  CourierDetailRoute,
  MigrationsRoute,
  AuditLogsRoute,
  OnboardingRoute,
  OnboardingTasksRoute,
  AttendanceRoute,
  AttendanceReportRoute,
  ClockInRoute,
  PayrollRoute,
  StaffRatesRoute,
  PayslipsRoute,
  CourierCustomersRoute,
  CustomerIssuesRoute,
  RewardsRoute,
  CashOutRoute,
  ShopForMeRoute,
  ShopRoute,
  ShopCategoriesRoute,
  ShopOrdersRoute,
  BlogPostsRoute,
  BlogCategoriesRoute,
  PackageRequestsRoute,
  FaqManagementRoute,
  SupportTicketsRoute,
  DevBoardRoute,
  ProfileRoute,
  UnauthorizedRoute,
  HelpCenterRoute,
  MoreRoute,
  ChangelogRoute,
  RefreshRoute,
} from "./config/routePaths";

// Lazy-loaded page components
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Customers = lazy(() => import("./pages/Customers"));
const Packages = lazy(() => import("./pages/Packages"));
const Pos = lazy(() => import("./pages/Pos"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Manifests = lazy(() => import("./pages/Manifests"));
const ScanIn = lazy(() => import("./pages/ScanIn"));
const SmartScan = lazy(() => import("./pages/SmartScan"));
const DeliveryRequest = lazy(() => import("./pages/DeliveryRequest"));
const Communication = lazy(() => import("./pages/Communication"));
const CommunicationLogs = lazy(() => import("./pages/CommunicationLogs"));
const Whatsapp = lazy(() => import("./pages/Whatsapp"));
const Marketing = lazy(() => import("./pages/Marketing").then(m => ({ default: m.Marketing })));
const MarketingAgents = lazy(() => import("./pages/MarketingAgents"));
const ReviewsGallery = lazy(() => import("./pages/ReviewsGallery"));
const Expenses = lazy(() => import("./pages/Expenses"));
const CustomsEstimator = lazy(() => import("./pages/CustomsEstimator"));
const TariffCodes = lazy(() => import("./pages/TariffCodes"));
const Warehouse = lazy(() => import("./pages/Warehouse"));
const WarehouseHub = lazy(() => import("./pages/WarehouseHub"));
const ScanBonusReport = lazy(() => import("./pages/ScanBonusReport"));
const WarehouseBilling = lazy(() => import("./pages/WarehouseBilling"));
const WarehouseInvoicesPage = lazy(() => import("./pages/WarehouseInvoices"));
const InvoiceRequestsPage = lazy(() => import("./pages/InvoiceRequests"));
const WarehouseInterests = lazy(() => import("./pages/WarehouseInterests"));
const UnknownPackages = lazy(() => import("./pages/UnknownPackages"));
const FulfillmentPartners = lazy(() => import("./pages/FulfillmentPartners"));
const FulfillmentOrders = lazy(() => import("./pages/FulfillmentOrders"));
const PartnerRequirements = lazy(() => import("./pages/PartnerRequirements"));
const PartnerCompliance = lazy(() => import("./pages/PartnerCompliance"));
const Users = lazy(() => import("./pages/Users"));
const Settings = lazy(() => import("./pages/Settings"));
const Billing = lazy(() => import("./pages/Billing").then(m => ({ default: m.Billing })));
const Couriers = lazy(() => import("./pages/Couriers"));
const CourierDetail = lazy(() => import("./pages/CourierDetail"));
const Migrations = lazy(() => import("./pages/Migrations"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const OnboardingTasks = lazy(() => import("./pages/OnboardingTasks"));
const Attendance = lazy(() => import("./pages/Attendance"));
const AttendanceReport = lazy(() => import("./pages/AttendanceReport"));
const ClockIn = lazy(() => import("./pages/ClockIn"));
const Payroll = lazy(() => import("./pages/Payroll"));
const StaffRates = lazy(() => import("./pages/StaffRates"));
const Payslips = lazy(() => import("./pages/Payslips"));
const CourierCustomers = lazy(() => import("./pages/CourierCustomers"));
const CustomerIssues = lazy(() => import("./pages/CustomerIssues"));
const Rewards = lazy(() => import("./pages/Rewards"));
const CashOut = lazy(() => import("./pages/CashOut"));
const Profile = lazy(() => import("./pages/Profile"));
const Unauthorized = lazy(() => import("./pages/Unauthorized"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const More = lazy(() => import("./pages/More"));
const Changelog = lazy(() => import("./pages/Changelog"));
const Refresh = lazy(() => import("./pages/Refresh"));
const LocalDeliveries = lazy(() => import("./pages/LocalDeliveries"));
const ShopForMe = lazy(() => import("./pages/ShopForMe"));
const Shop = lazy(() => import("./pages/Shop"));
const ShopCategories = lazy(() => import("./pages/ShopCategories"));
const ShopOrders = lazy(() => import("./pages/ShopOrders"));
const BlogPosts = lazy(() => import("./pages/BlogPosts"));
const BlogCategories = lazy(() => import("./pages/BlogCategories"));
const FaqManagement = lazy(() => import("./pages/FaqManagement"));
const SupportTickets = lazy(() => import("./pages/SupportTickets"));
const DevBoard = lazy(() => import("./pages/DevBoard"));
const PackageRequests = lazy(() => import("./pages/PackageRequests"));
const Assistant = lazy(() => import("./pages/Assistant"));

/**
 * Wraps the routed content in an ErrorBoundary keyed on the current route, so a
 * render error in one page shows a contained fallback (instead of a blank white
 * page) and automatically clears when the user navigates elsewhere.
 */
const RoutedErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  return <ErrorBoundary resetKeys={[location.pathname]}>{children}</ErrorBoundary>;
};

export function App() {
  const dispatch = useDispatch<AppDispatch>();
  const isCourierLoading = useSelector(getIsCourierLoading);
  const courierError = useSelector(getCourierError);
  const courier = useSelector(getCourier);
  const bootstrapView = getAppBootstrapView({
    isLoading: isCourierLoading,
    hasCourier: !!courier,
    hasError: !!courierError,
  });

  // Check for app updates and notify users
  useVersionCheck();

  // The bootstrap below runs while the admin is still logged out, so it cannot
  // ask whether this courier is partner-linked ($authenticated-only). Resolve
  // it once the user signs in — login is a client-side navigate(), so nothing
  // else re-runs fetchCourier.
  usePartnerLinkRefresh();

  // Handle ?refresh=true query param - force update PWA
  useEffect(() => {
    if (checkForRefreshParam()) {
      forceRefresh();
    }
  }, []);

  useEffect(() => {
    // Initialize auth state from localStorage
    dispatch(init());
    // Fetch courier data
    dispatch(fetchCourier());
  }, [dispatch]);

  if (bootstrapView === 'loading') {
    return <Loading text='Getting things ready...' />;
  }

  if (bootstrapView === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Oops! Something's Not Quite Right <span role="img" aria-label="thinking face">🤔</span>.
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              We couldn't find your courier information. This might be because:
            </p>
            <ul className="mt-4 text-sm text-gray-600 list-disc list-inside">
              <li>You're trying to access from an incorrect URL</li>
              <li>Our systems are taking a coffee break <span role="img" aria-label="coffee">☕</span></li>
              <li>The internet gremlins are at it again <span role="img" aria-label="ghost">👻</span></li>
            </ul>
            <p className="mt-4 text-sm text-gray-600">
              Try refreshing the page, or contact support if the issue persists.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const renderProtectedRoute = (element: React.ReactElement, allowedRoles: string[], requiresApiKey = false, requiresFeatureFlag?: string) => (
    <ProtectedRoute
      element={element}
      allowedRoles={allowedRoles}
      requiresApiKey={requiresApiKey}
      requiresFeatureFlag={requiresFeatureFlag}
    />
  );

  return (
    <ThemeProvider>
      <PocketBaseProvider>
        <Router>
          <ScrollToTop />
          <OnboardingRedirect>
            <div className="bg-gray-50 dark:bg-gray-900 h-full overflow-hidden transition-colors duration-200">
              <Toaster position="top-right" />
              <RoutedErrorBoundary>
              <Suspense fallback={<Loading text="Loading..." />}>
                <Routes>
                {/* Auth Route */}
                <Route path={AuthRoutePath} element={<Auth />} />
                <Route path={ResetPasswordRoutePath} element={<Auth />} />

                {/* Refresh Route - Force PWA update */}
                <Route path={RefreshRoute} element={<Refresh />} />

                {/* Protected Routes */}
                <Route path={DashboardRoute} element={renderProtectedRoute(<Dashboard />, ['Overlord', 'Administrator'])} />
                <Route path={PosRoute} element={renderProtectedRoute(<Pos />, ['Overlord', 'Cashier', 'Administrator', 'Store Administrator', 'Limited Cashier'])} />
                <Route path={PackageRequestsRoute} element={renderProtectedRoute(<PackageRequests />, ['Overlord', 'Administrator', 'Cashier', 'Store Administrator', 'Warehouse Administrator', 'Limited Cashier'], false, 'isPackageRequestsEnabled')} />
                <Route path={CustomersRoute} element={renderProtectedRoute(<Customers />, ['Overlord', 'Administrator', 'Cashier', 'Store Administrator'])} />
                <Route path={`${CustomersRoute}/:id`} element={renderProtectedRoute(<Customers />, ['Overlord', 'Administrator', 'Cashier', 'Store Administrator'])} />
                <Route path={ManifestsRoute} element={renderProtectedRoute(<Manifests />, ['Overlord', 'Administrator', 'Cashier'])} />
                <Route path={PackagesRoute} element={renderProtectedRoute(<Packages />, ['Overlord', 'Administrator', 'Cashier', 'Warehouse Administrator', 'Store Administrator', 'Limited Cashier'])} />
                <Route path={`${PackagesRoute}/:id`} element={renderProtectedRoute(<Packages />, ['Overlord', 'Administrator', 'Cashier', 'Warehouse Administrator', 'Store Administrator', 'Limited Cashier'])} />
                <Route path={ScanInRoute} element={renderProtectedRoute(<ScanIn />, ['Overlord', 'Administrator', 'Cashier', 'Warehouse Administrator', 'Store Administrator',])} />
                <Route path={SmartScanRoute} element={renderProtectedRoute(<SmartScan />, ['Overlord', 'Administrator', 'Cashier', 'Warehouse Administrator', 'Store Administrator',], false, 'isSmartScanEnabled')} />
                <Route path={DeliveryRequestRoute} element={renderProtectedRoute(<DeliveryRequest />, ['Overlord', 'Administrator', 'Cashier'])} />
                <Route path={LocalDeliveriesRoute} element={renderProtectedRoute(<LocalDeliveries />, ['Overlord', 'Administrator', 'Cashier'])} />
                <Route path={InvoicesRoute} element={renderProtectedRoute(<Invoices />, ['Overlord', 'Administrator', 'Cashier', 'Store Administrator'])} />
                <Route path={`${InvoicesRoute}/:id`} element={renderProtectedRoute(<Invoices />, ['Overlord', 'Administrator', 'Cashier', 'Store Administrator'])} />
                <Route path={CommunicationRoute} element={renderProtectedRoute(<Communication />, ['Overlord', 'Administrator', 'Store Administrator'])} />
                <Route path={CommunicationLogsRoute} element={renderProtectedRoute(<CommunicationLogs />, ['Overlord', 'Administrator', 'Store Administrator'])} />
                <Route path={MarketingRoute} element={renderProtectedRoute(<Marketing />, ['Overlord', 'Administrator', 'Store Administrator'])} />
                <Route path={ReviewsGalleryRoute} element={renderProtectedRoute(<ReviewsGallery />, ['Overlord', 'Administrator', 'Store Administrator'])} />
                <Route path={ExpensesRoute} element={renderProtectedRoute(<Expenses />, ['Overlord', 'Administrator'], false, 'isExpenseTrackerEnabled')} />
                <Route path={CustomsEstimatorRoute} element={renderProtectedRoute(<CustomsEstimator />, ['Overlord', 'Administrator', 'Cashier'], false, 'isCustomsCalculatorEnabled')} />
                <Route path={UsersRoute} element={renderProtectedRoute(<Users />, ['Overlord', 'Administrator'])} />
                <Route path={WarehouseRoute} element={renderProtectedRoute(<Warehouse />, ['Overlord', 'Administrator', 'Warehouse Administrator'], true)} />
                <Route path={WarehouseHubRoute} element={renderProtectedRoute(<WarehouseHub />, ['Overlord', 'Administrator', 'Warehouse Administrator'], false, 'isWarehouseApiEnabled')} />
                <Route path={ScanBonusReportRoute} element={renderProtectedRoute(<ScanBonusReport />, ['Overlord', 'Administrator', 'Warehouse Administrator'], false, 'isWarehouseApiEnabled')} />
                <Route path={WarehouseBillingRoute} element={renderProtectedRoute(<WarehouseBilling />, ['Overlord', 'Administrator', 'Warehouse Administrator'])} />
                <Route path={WAREHOUSE_INVOICES_ROUTE} element={renderProtectedRoute(<WarehouseInvoicesPage />, ['Overlord', 'Administrator'])} />
                <Route path={InvoiceRequestsRoute} element={renderProtectedRoute(<InvoiceRequestsPage />, ['Overlord', 'Administrator', 'Cashier'])} />
                <Route path={WarehouseInterestsRoute} element={renderProtectedRoute(<WarehouseInterests />, ['Overlord', 'Administrator', 'Warehouse Administrator'])} />
                <Route path={FulfillmentPartnersRoute} element={renderProtectedRoute(<FulfillmentPartners />, ['Overlord', 'Administrator', 'Warehouse Administrator'], false, 'isFulfillmentEnabled')} />
                <Route path={FulfillmentOrdersRoute} element={renderProtectedRoute(<FulfillmentOrders />, ['Overlord', 'Administrator', 'Warehouse Administrator'], false, 'isFulfillmentEnabled')} />
                <Route path={UnknownPackagesRoute} element={renderProtectedRoute(<UnknownPackages />, ['Overlord', 'Administrator', 'Warehouse Administrator'])} />
                <Route path={PartnerRequirementsRoute} element={renderProtectedRoute(<PartnerRequirements />, ['Overlord', 'Administrator'])} />
                <Route path={PartnerComplianceRoute} element={renderProtectedRoute(<PartnerCompliance />, ['Overlord', 'Administrator', 'Warehouse Administrator'])} />
                <Route path={SettingsRoute} element={renderProtectedRoute(<Settings />, ['Overlord', 'Administrator'])} />
                <Route path={MigrationsRoute} element={renderProtectedRoute(<Migrations />, ['Overlord', 'Administrator'])} />
                <Route path={BillingRoute} element={renderProtectedRoute(<Billing />, ['Overlord', 'Administrator'])} />
                <Route path={ProfileRoute} element={renderProtectedRoute(<Profile />, [])} />
                <Route path={WhatsappRoute} element={renderProtectedRoute(<Whatsapp />, ['Overlord', 'Administrator', 'Cashier', 'Store Administrator', 'Limited Cashier'])} />
                <Route path={AssistantRoute} element={renderProtectedRoute(<Assistant />, ['Overlord', 'Administrator'], false, 'isCustomerChatEnabled')} />
                <Route path={CourierDetailRoute} element={renderProtectedRoute(<CourierDetail />, ['Overlord'])} />
                <Route path={CouriersRoute} element={renderProtectedRoute(<Couriers />, ['Overlord'])} />
                <Route path={CourierCustomersRoute} element={renderProtectedRoute(<CourierCustomers />, ['Overlord', 'Administrator', 'Warehouse Administrator'])} />
                <Route path={CustomerIssuesRoute} element={renderProtectedRoute(<CustomerIssues />, ['Overlord', 'Administrator', 'Super Administrator', 'Cashier', 'Store Administrator', 'Warehouse Administrator', 'Limited Cashier'])} />
                <Route path={RewardsRoute} element={renderProtectedRoute(<Rewards />, ['Overlord', 'Administrator'])} />
                <Route path={CashOutRoute} element={renderProtectedRoute(<CashOut />, ['Overlord', 'Administrator', 'Cashier', 'Store Administrator', 'Limited Cashier'])} />
                <Route path={ShopForMeRoute} element={renderProtectedRoute(<ShopForMe />, ['Overlord', 'Administrator', 'Cashier'])} />
                <Route path={ShopRoute} element={renderProtectedRoute(<Shop />, ['Overlord', 'Administrator', 'Cashier'])} />
                <Route path={ShopCategoriesRoute} element={renderProtectedRoute(<ShopCategories />, ['Overlord', 'Administrator', 'Cashier'])} />
                <Route path={ShopOrdersRoute} element={renderProtectedRoute(<ShopOrders />, ['Overlord', 'Administrator', 'Cashier'])} />
                <Route path={BlogPostsRoute} element={renderProtectedRoute(<BlogPosts />, ['Overlord', 'Administrator'], false, 'isBlogEnabled')} />
                <Route path={BlogCategoriesRoute} element={renderProtectedRoute(<BlogCategories />, ['Overlord', 'Administrator'], false, 'isBlogEnabled')} />
                <Route path={FaqManagementRoute} element={renderProtectedRoute(<FaqManagement />, ['Overlord', 'Administrator'])} />
                <Route path={SupportTicketsRoute} element={renderProtectedRoute(<SupportTickets />, ['Overlord', 'Administrator'])} />
                <Route path={DevBoardRoute} element={renderProtectedRoute(<DevBoard />, ['Overlord'])} />
                <Route path={TariffCodesRoute} element={renderProtectedRoute(<TariffCodes />, ['Overlord'])} />
                <Route path={HelpCenterRoute} element={renderProtectedRoute(<HelpCenter />, [])} />
                <Route path={AuditLogsRoute} element={renderProtectedRoute(<AuditLogs />, ['Overlord', 'Administrator'])} />
                <Route path={AttendanceRoute} element={renderProtectedRoute(<Attendance />, ['Overlord', 'Administrator', 'Store Administrator'])} />
                <Route path={AttendanceReportRoute} element={renderProtectedRoute(<AttendanceReport />, ['Overlord', 'Administrator'])} />
                <Route path={ClockInRoute} element={renderProtectedRoute(<ClockIn />, ['Overlord', 'Administrator', 'Cashier', 'Store Administrator', 'Warehouse Administrator', 'Limited Cashier'])} />
                <Route path={PayrollRoute} element={renderProtectedRoute(<Payroll />, ['Overlord', 'Administrator'])} />
                <Route path={StaffRatesRoute} element={renderProtectedRoute(<StaffRates />, ['Overlord', 'Administrator'])} />
                <Route path={PayslipsRoute} element={renderProtectedRoute(<Payslips />, ['Overlord', 'Administrator', 'Cashier', 'Store Administrator', 'Warehouse Administrator', 'Limited Cashier'])} />
                <Route path={MoreRoute} element={renderProtectedRoute(<More />, [])} />
                <Route path={ChangelogRoute} element={renderProtectedRoute(<Changelog />, [])} />
                <Route path={OnboardingRoute} element={renderProtectedRoute(<Onboarding />, ['Overlord', 'Administrator'])} />
                <Route path={OnboardingTasksRoute} element={renderProtectedRoute(<OnboardingTasks />, ['Overlord'])} />
                <Route path={MarketingAgentsRoute} element={renderProtectedRoute(<MarketingAgents />, ['Overlord', 'Administrator'])} />
                <Route path={UnauthorizedRoute} element={<Unauthorized />} />

                {/* Unknown Route - Redirect to Dashboard */}
                <Route path="*" element={<Navigate to={DashboardRoute} replace />} />

                </Routes>
              </Suspense>
              </RoutedErrorBoundary>
            </div>
          </OnboardingRedirect>
        </Router>
      </PocketBaseProvider>
    </ThemeProvider>
  );
}

export default App;
